import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const roleLabels = {
  notary: { label: 'Notario', color: 'badge-primary' },
  supervisor: { label: 'Supervisor', color: 'badge-info' },
  staff: { label: 'Staff', color: 'badge-secondary' },
};

const rolePermisos = {
  notary: 'Acceso total al sistema',
  supervisor: 'Todos los clientes y expedientes, sin configuración',
  staff: 'Solo sus propios clientes y expedientes',
};

const emptyForm = { name: '', email: '', password: '', role: 'staff' };

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(emptyForm);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const handleNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = (user) => {
    setEditing(user);
    setForm({ name: user.name, email: user.email, password: '', role: user.role });
    setErrors({});
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, form);
      } else {
        await api.post('/users', form);
      }
      handleCancel();
      fetchUsers();
    } catch (error) {
      if (error.response?.status === 422) setErrors(error.response.data.errors);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`¿Eliminar a ${user.name}?`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Error al eliminar');
    }
  };

  return (
    <div className="container-fluid">
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Usuarios</h4>
        <button className="btn btn-primary btn-sm" onClick={handleNew}>
          <i className="ti-plus mr-1"></i> Nuevo Usuario
        </button>
      </div>

      {showForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">{editing ? 'Editar Usuario' : 'Nuevo Usuario'}</h6>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>Nombre <span className="text-danger">*</span></label>
                    <input type="text" name="name"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      value={form.name} onChange={handleChange} required />
                    {errors.name && <div className="invalid-feedback">{errors.name[0]}</div>}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>Correo <span className="text-danger">*</span></label>
                    <input type="email" name="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      value={form.email} onChange={handleChange} required />
                    {errors.email && <div className="invalid-feedback">{errors.email[0]}</div>}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>{editing ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
                    <input type="password" name="password"
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      value={form.password} onChange={handleChange} required={!editing} />
                    {errors.password && <div className="invalid-feedback">{errors.password[0]}</div>}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>Rol <span className="text-danger">*</span></label>
                    <select name="role" className="form-control" value={form.role} onChange={handleChange}>
                      <option value="staff">Staff — Solo sus clientes y expedientes</option>
                      <option value="supervisor">Supervisor — Todos los clientes y expedientes</option>
                      <option value="notary">Notario — Acceso total</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                  {submitting ? 'Guardando...' : 'Guardar'}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body p-0 mobile-card-list">
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
          ) : (
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th className="mobile-hide">Correo</th>
                  <th>Rol</th>
                  <th className="mobile-hide">Permisos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="td-name">
                      {user.name}
                      {/* Email visible debajo del nombre en móvil */}
                      <div className="d-md-none" style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        {user.email}
                      </div>
                      {/* Permisos visibles debajo en móvil */}
                      <div className="d-md-none mt-1">
                        <small className="text-muted">{rolePermisos[user.role]}</small>
                      </div>
                    </td>
                    <td className="mobile-hide">{user.email}</td>
                    <td data-label="Rol">
                      <span className={`badge ${roleLabels[user.role]?.color}`}>
                        {roleLabels[user.role]?.label}
                      </span>
                    </td>
                    <td className="mobile-hide">
                      <small className="text-muted">{rolePermisos[user.role]}</small>
                    </td>
                    <td className="td-actions">
                      <div className="d-flex" style={{ gap: '6px' }}>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(user)}>
                          <i className="ti-pencil"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(user)}>
                          <i className="ti-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;